import { type Clip } from '@air/api/types';
import { ClipUtils } from '@air/utils-clips';

import { type FilterImageAssetsResult, type VideoRejectionReason } from './canvasAssetDropTypes';
import { clipToCanvasAssetPayload } from './clipToCanvasAssetPayload';
import { filterImageAssets } from './filterImageAssets';
import { getVideoLimitDetail } from './getAssetDropToastMessage';

const getFilterResult = (clips: Clip[]): FilterImageAssetsResult =>
  filterImageAssets(clips.map(clipToCanvasAssetPayload));

const getVideoDisabledReason = (rejections: VideoRejectionReason[]): string => {
  const detail = getVideoLimitDetail(rejections);

  return detail ? `Video ${detail}` : 'Video exceeds size or duration limit';
};

export type AddToCanvasMenuState = {
  canAdd: boolean;
  disabledReason: string | undefined;
};

const getAddToCanvasEligibility = (clips: Clip[]) => {
  if (clips.length === 0) {
    return { canAdd: false, filterResult: undefined };
  }

  const filterResult = getFilterResult(clips);

  return { canAdd: filterResult.addedCount > 0, filterResult };
};

const getDisabledReason = (
  clips: Clip[],
  canAdd: boolean,
  filterResult: FilterImageAssetsResult | undefined,
): string | undefined => {
  if (canAdd) {
    return undefined;
  }

  if (clips.length === 0 || !filterResult) {
    return 'Unsupported file type';
  }

  if (clips.every((clip) => ClipUtils.isProcessing(clip.status))) {
    return 'Processing…';
  }

  const { unsupportedCount, videoRejections } = filterResult;

  if (videoRejections.length > 0 && unsupportedCount === 0) {
    return getVideoDisabledReason(videoRejections);
  }

  return 'Unsupported file type';
};

/** Eligibility and disabled copy for add-to-canvas menu items in a single filter pass. */
export const getAddToCanvasMenuState = (clips: Clip[]): AddToCanvasMenuState => {
  const { canAdd, filterResult } = getAddToCanvasEligibility(clips);

  return {
    canAdd,
    disabledReason: getDisabledReason(clips, canAdd, filterResult),
  };
};
